import React, { useMemo } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { DocumentWithDownloadUrl as Document } from '@/client';
import BaseDocumentEdit from './BaseDocumentEdit';
import FileViewer from '../viewers/FileViewer';
import { isImage, isPdf } from '@/utils/documentUtils';
import { isBrowserPreviewableImageByNameOrMime } from '@/utils/upload/uploadUtils.ts';
import CitedPageChips from '@/components/documents/viewers/CitedPageChips';
import { useFullscreenPageRequest } from '@/hooks/viewers/useFullscreenPageRequest';

interface DocumentFileEditProps {
	document: Document;
	citedPages?: number[];
	onVisibilityChange?: (isPrivate: boolean) => void;
	/** True when the document is a pending share — hides its not-yet-available summary/tags sections. */
	isPendingShare?: boolean;
}

const EMPTY_CITED_PAGES: number[] = [];

/**
 * Component for displaying file documents with preview functionality
 * Supports PDF and common image formats; other types show "Preview not available".
 */
const DocumentFileEdit: React.FC<DocumentFileEditProps> = ({
	document,
	citedPages = EMPTY_CITED_PAGES,
	onVisibilityChange,
	isPendingShare = false,
}) => {
	const downloadUrl = document.download_url;
	const canPreview =
		(isPdf(document) || (isImage(document) && isBrowserPreviewableImageByNameOrMime(document.filename))) &&
		!!downloadUrl;
	const { request: fullscreenPageRequest, requestPage } = useFullscreenPageRequest();
	const firstCitedPage = citedPages[0] ?? 1;
	const showCitedPages = citedPages.length > 0 && isPdf(document);

	/** Memoize the file preview so it doesn't remount when unrelated document fields (e.g. private) change */
	const fileContent = useMemo(() => {
		if (canPreview && downloadUrl) {
			return (
				<Box position="relative">
					{showCitedPages && (
						<Box position="absolute" top={3} left={3} zIndex={1} maxW="calc(100% - 24px)">
							<CitedPageChips pages={citedPages} onOpenPage={requestPage} />
						</Box>
					)}
					<FileViewer
						fileUrl={downloadUrl}
						title={document.filename || 'Document'}
						filename={document.filename}
						showPageControls={false}
						initialPage={firstCitedPage}
						fullscreenPageRequest={fullscreenPageRequest}
					/>
				</Box>
			);
		}

		return (
			<Box p={4} textAlign="center">
				<Text textStyle="body.sm" color="fg.muted">
					Preview not available
				</Text>
			</Box>
		);
	}, [
		canPreview,
		citedPages,
		document.filename,
		downloadUrl,
		firstCitedPage,
		fullscreenPageRequest,
		requestPage,
		showCitedPages,
	]);

	return (
		<BaseDocumentEdit document={document} onVisibilityChange={onVisibilityChange} isPendingShare={isPendingShare}>
			{fileContent}
		</BaseDocumentEdit>
	);
};

export default DocumentFileEdit;
